<template>
  <ProgressLoader
    v-if="loading"
    mode="indeterminate"
    color="#5AC396"
    size="150px"
  />

  <div v-else>
    <AuBreadcrumb
      :pages="breadcrumb"
    />

    <Form :footer-flex="!withdrawSuccess ? 'flex-end' : 'flex-start'">
      <template #title>
        {{ $t("withdraw.title") }}
      </template>

      <div v-if="kycError">
        <div>
          {{ $t("withdraw.kycError") }}
        </div>

        <RouterLink
          to="/user"
        >
          <AuButton
            class="mt-2"
            type="primary"
            center
            width="300px"
          >
            <div>
              {{ kycError }}
            </div>
          </AuButton>
        </RouterLink>
      </div>

      <div v-else-if="!withdrawSuccess">
        <div class="body">
          <div v-if="showSelect">
            <div class="select-label">
              {{ $t("withdraw.selectLabel") }}
            </div>
            <AuSelect
              :value="selectedToken"
              :options="tokenOptions"
              @input="select"
            />
          </div>
          <div v-if="stage === 1">
            <div class="select-label">
              {{ $t("withdraw.netLabel") }}
            </div>
            <AuSelect
              :value="selectedNetwork"
              :options="networkOptions"
              @input="selectNet"
            />
          </div>
          <div v-if="stage === 1">
            <AuInput
              v-model="eth"
              required
              :label="$t('withdraw.ethLabel')"
            />
            <span class="notice">
              {{ $t("withdraw.ethNotice") }}
            </span>
          </div>
          <div v-if="stage === 1">
            <AuInput
              :model-value="amountFixed"
              :label="$t('withdraw.sumLabel')"
              type="Mask"
              :mask="amountRegex"
              required
              @update:modelValue="setAmount"
            />

            <div
              class="convert-box"
            >
              <span class="exception">
                {{ $t("withdraw.sumNotice") }}
                <span class="course-text">
                  {{ availableFormat }}  {{ selectedTokenMnemonic }}
                </span>
              </span>
              <span class="exception">
                {{ $t("convertToken.commission") + ": " }}
                <span
                  v-if="selectedTokenMnemonic == 'USD.au'"
                  class="course-text"
                >
                  {{ 1 }} {{ selectedTokenMnemonic }}
                </span>
                <span
                  v-if="selectedTokenMnemonic == 'AUT.au'"
                  class="course-text"
                >
                  {{ 100 }} {{ selectedTokenMnemonic }}
                </span>
              </span>
              <!-- <span>
                {{ $t("convertToken.willReceive") + ": " }}
                <span class="course-text">
                  {{ receive + " " + selectedTokenMnemonic }}
                </span>
              </span> -->
            </div>
          </div>
          <span v-if="stage===2">
            {{ message }}
          </span>
          <AuInput
            v-if="stage===2"
            v-model="code"
            :label="$t('withdraw.confirmCode')"
          />
          <span
            v-if="error"
            class="error"
          >
            {{ error }}
          </span>
        </div>
      </div>
      <div
        v-if="withdrawSuccess"
        class="success"
      >
        <span class="msg">{{ $t("withdraw.success") }}</span>
      </div>

      <template
        v-if="!withdrawSuccess && !kycError"
        #footer
      >
        <AuButton
          v-if="stage===2 && !use2fa"
          class="btn"
          center
          :disabled="timer !== 0"
          @click="sendMail"
        >
          {{ sendBtnText }}
        </AuButton>
        <AuButton
          type="primary"
          class="btn"
          center
          :is-progress="progress"
          @click="doAction"
        >
          {{ withdrawBtnText }}
        </AuButton>
      </template>

      <template
        v-else-if="!kycError"
        #footer
      >
        <AuButton
          type="primary"
          center
          @click="goToBriefcase"
        >
          {{ $t("withdraw.backBtnText") }}
        </AuButton>
      </template>
    </Form>
  </div>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import { mapActions, mapGetters } from "vuex";

import ProgressLoader from "@/loaders/progress-bar";
import { checkAccess, scope } from "@/help/accessData";

export default {
  name: "Withdraw",
  components: {
    ProgressLoader
  },
  data() {
    return {
      breadcrumb: [
        { key: 0, name: this.$t("router.pages.dashboard"), path: "/" },
        { key: 1, name: this.$t("router.pages.briefcase"), path: "/my-assets" }
      ],
      stage: 1,
      selectedToken: null,
      selectedNetwork: "bsc",
      networkOptions: [
        {
          display: "Binance network (BSC)",
          value: "bsc"
        },
        {
          display: "Avalanche C-chain",
          value: "avax"
        }
      ],
      eth: null,
      amount: "",
      code: "",
      error: "",
      currency: "1",
      available: 0,
      availableFormat: 0,
      decimals: 2,
      withdrawSuccess: false,
      timer: 0,
      sendBtnText: this.$t("withdraw.sendCode"),
      progress: false,
      interval: null,
      eventId: null,
      loading: true
    };
  },

  computed: {
    ...mapGetters({
      defaultTokenId: "defaultToken/defaultTokenId",
      defaultTokenMnemonic: "defaultToken/defaultTokenMnemonic",
      ethAddresses: "ethAddresses",
      tokens: "tokens"
    }),

    showSelect() {
      return this.stage === 1 && process.env.VUE_APP_AUT_WITHDRAWAL_ENABLED == "true";
    },

    message() {
      return this.use2fa ? this.$t("withdraw.twoFAMessage") : this.$t("withdraw.mailMessage");
    },

    withdrawBtnText() {
      return this.stage === 1 ? this.$t("withdraw.btnNext") : this.$t("withdraw.btnWithdraw");
    },

    use2fa() {
      return this.$auth.user().is2faEnabled;
    },

    selectedTokenDecimals() {
      return _.find(this.tokens, token => token.id === this.selectedToken)?.decimals;
    },

    selectedTokenMnemonic() {
      return _.find(this.tokens, token => token.id === this.selectedToken)?.mnemonicEng;
    },

    tokenOptions() {
      return _.reduce(this.tokens, (res, token) => {
        return token.mnemonicEng === this.defaultTokenMnemonic || token.mnemonicEng === this.autMnemonic ?
          [...res, { display: token.mnemonicEng, value: token.id }] : res;
      }, []);
    },

    autMnemonic() {
      return process.env.VUE_APP_AUT_MNEMONIC;
    },

    amountRegex() {
      return new RegExp(`^\\d*.?\\d{0,${this.decimals}}$`);
    },

    receive() {
      const amount = new BigNumber(this.amountFixed || 0);
      if (amount.isNaN()) {
        return "";
      }

      // Вычитаем 1 для USD.au или 100 для AUT.au
      const deductedAmount = this.selectedTokenMnemonic === "USD.au"
        ? amount.minus(1)
        : this.selectedTokenMnemonic === "AUT.au"
          ? amount.minus(100)
          : amount;

      const commission = new BigNumber(this.commission || 0);
      const amountAfterCommission = deductedAmount.multipliedBy(
        new BigNumber(1).minus(commission.dividedBy(100))
      );

      const rate = new BigNumber(this.rate || 1);
      const finalAmount = amountAfterCommission.multipliedBy(rate);

      return finalAmount.isNaN() ? "" : finalAmount.toFixed(2);
    },

    amountFixed() {
      if (!this.amount) {
        return "";
      }
      else {
        return BigNumber(this.amount).dividedBy(BigNumber(10).pow(this.decimals)).toString();
      }
    },

    kycError() {
      if (checkAccess(scope.kycLimitsEnabled) && !checkAccess(scope.kycComplete)) {
        return this.$t("kyc.comeThrough");
      }

      return "";
    }
  },

  async created() {
    await this.select(this.defaultTokenId);
    this.loading = false;
  },

  methods: {
    ...mapActions({
      getAuOwnerByTokenId: "getAuOwnerByTokenId",
      sendTokenEvents: "sendTokenEvents",
      sendMailConfirmation: "withdrawal/sendMailConfirmation",
      getSystemSettings: "getSystemSettings",
      confirmMailCode: "withdrawal/confirmMailCode"
    }),

    setAmount(value) {
      if (isNaN(value) || value == "") {
        this.amount = "";
      }
      else {
        this.amount = BigNumber(value).multipliedBy(BigNumber(10).pow(this.decimals)).toString();
      }
    },

    async doAction() {
      switch (this.stage) {
        case 1:
          if (!this.$filters.correctETH(this.eth)) {
            this.error = this.$t("withdraw.errorCorrectEth");
            return;
          }
          // if (this.amount === "" || this.amount == 0 || !this.amount || BigNumber(this.amount).isGreaterThan(this.available)) {
          //   this.error = this.$t("withdraw.errorCorrectSum");
          //   return;
          // }
          this.error = "";
          this.progress = true;
          this.stage = this.stage + await this.withdraw();
          if (!this.use2fa && this.stage === 2) {
            this.startTimer();
          }
          this.progress = false;
          break;
        case 2:
          this.progress = true;
          await this.check();
          this.progress = false;
          break;
      }
    },

    async check() {
      const params = {
        code: this.code,
        tokenEvent: `/api/token_events/${this.eventId}`
      };
      try {
        await this.confirmMailCode(params);
        this.withdrawSuccess = true;

        try {
          if (window.carrotquest && process.env.VUE_APP_CARROT_ENABLED === "true") {
            window.carrotquest.track("withdraw", {
              "address": this.eth,
              "amount": this.amount
            });
          }
        }
        catch (error) {
          console.error(error);
        }

        if (this.interval) {
          clearInterval(this.interval);
        }
      }
      catch (e) {
        this.error = this.$t("withdraw.incorrectCode");
      }
    },

    goToBriefcase() {
      this.$router.push({ name: "briefcase" });
    },

    startTimer() {
      this.timer = 60;
      this.interval = setInterval(() => {
        this.timer--;
        this.sendBtnText = this.$t("withdraw.sendAgain") + this.timer + this.$t("withdraw.sec");
        if (this.timer === 0) {
          this.sendBtnText = this.$t("withdraw.sendCode");
          clearInterval(this.interval);
        }
      }, 1000);
    },

    async sendMail() {
      try {
        await this.sendMailConfirmation(this.eventId);
        this.startTimer();
        this.error = "";
      }
      catch (e) {
        this.error = e.response?.data?.message;
      }
    },

    async select(value) {
      this.selectedToken = value;
      await this.getAvailableSum();
    },
    async selectNet(value) {
      this.selectedNetwork = value;
    },

    async getAvailableSum() {
      try {
        const resp = await this.getAuOwnerByTokenId({
          tokenId: this.selectedToken,
          ownerAddress: this.ethAddresses[0].value
        });
        this.available = resp[0].amount;
        this.decimals = resp[0].auToken.decimals;
        let buf = BigNumber(this.available).dividedBy(BigNumber(10).pow(this.decimals));
        this.availableFormat = buf.isNaN() ? 0 : buf.toString(10);
      }
      catch (e) {
        this.available = 0;
        this.availableFormat = 0;
      }
    },

    async withdraw() {
      let params = {
        user: `api/users/${this.$auth.user().id}`,
        createdAt: new Date,
        type: "token_withdrawal",
        description: "withdraw",
        value: this.amount,
        token: `/api/au_tokens/${this.selectedToken}`,
        isTask: true,
        mode: this.selectedNetwork,
        address: this.eth
      };

      if (checkAccess(scope.kycLimitsEnabled) && !checkAccess(scope.kycComplete)) {
        this.error = this.$t("withdraw.error") + this.$t("kyc.comeThrough");
        return 0;
      }

      try {
        const resp = await this.sendTokenEvents(params);
        if (resp?.status == 267) {
          this.error = this.$t("withdraw.error") + resp?.data?.message;
          return 0;
        }
        this.eventId = resp.data.id;
      }
      catch (e) {
        this.error = this.$t("withdraw.error") + e.response?.data?.message;
        return 0;
      }
      return 1;
    }
  }
};
</script>

<style scoped lang="scss">
.title {
  font-size: 24px;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .select-label {
    font-size: 14px;
    color: var(--color-50);
    margin-bottom: 8px;
  }

  .notice {
    font-size: 12px;
  }

  .error {
    width: 100%;
    height: 20px;
    text-align: center;
    font-size: 14px;
    color: red;
  }

  .btn-place {
    display: flex;
    flex-direction: row;
    justify-content: space-around;

    @media screen and (max-width: 680px) {
      flex-direction: column;
      align-items: center;
      gap: 30px
    }
  }

  .btn {
    width: 40%;
    @media screen and (max-width: 680px) {
      width: 100%;
    }
  }
}

.success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  margin-top: 50px;

  .msg {
    font-size: 20px;
    font-weight: 500;
    text-align: center;
  }
}
.convert-box {
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  :nth-child(even) {
    padding-bottom: 25px;
  }

  .exception {
    padding-bottom: 0;
  }
}
.course-text {
  color: #279063;
}
</style>